import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Users } from 'lucide-react';
import { organizationsAPI, usersAPI } from '../../services/api';

const AdminDashboard = () => {
  const { data: organizations = [], isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => organizationsAPI.getOrganizations(),
  });

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersAPI.getUsers(),
  });

  const stats = [
    {
      name: 'Total Organizations',
      value: organizations.length,
      icon: Building2,
      color: 'bg-blue-500',
      link: '/admin/organizations',
    },
    {
      name: 'Total Users',
      value: users.length,
      icon: Users,
      color: 'bg-green-500',
      link: '/admin/users',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Admin Dashboard</h1>
        <p className="text-gray-600 mt-1">Manage organizations and users</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {orgsLoading || usersLoading ? '...' : stat.value}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/admin/organizations"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Building2 className="h-5 w-5 text-blue-500 mr-3" />
            <span className="text-gray-700">Manage Organizations</span>
          </Link>
          <Link
            to="/admin/users"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Users className="h-5 w-5 text-green-500 mr-3" />
            <span className="text-gray-700">Manage Users</span>
          </Link>
        </div>
      </div>

      {/* Recent Organizations */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Recent Organizations</h2>
          <Link to="/admin/organizations" className="text-sm text-green-600 hover:text-green-700">
            View all
          </Link>
        </div>
        <div className="space-y-3">
          {organizations.slice(0, 5).map((org) => (
            <div key={org.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">
                  {org.name.charAt(0).toUpperCase()}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">{org.name}</p>
                  <p className="text-xs text-gray-500">{org.slug}</p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${org.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {org.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          ))}
          {organizations.length === 0 && !orgsLoading && (
            <p className="text-sm text-gray-500 text-center py-4">No organizations yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
